import { useEffect, useRef, useState } from 'react'
import { ArrowRight, Ellipsis, EyeOff, Sparkles } from 'lucide-react'
import { ContextMenu, MenuItem } from '@/components/ContextMenu'
import { renderMarkdown } from '@/lib/markdown'
import { splitLead } from '@/lib/telegramText'
import { releaseDate, releaseImageUrl, type ReleaseItem } from '@/lib/releasesApi'
import styles from './Release.module.css'

interface ViewProps {
  title: string
  items: ReleaseItem[]
  /** null — черновик: показываем сегодняшнюю дату, с ней он и выйдет. */
  publishedAt: string | null
}

/** Выпуск целиком: дата, заголовок, пункты с картинками. Одинаков в окне, в истории и в предпросмотре. */
export function ReleaseView({ title, items, publishedAt }: ViewProps) {
  return (
    <article className={styles.release}>
      <header className={styles.head}>
        <time className={styles.date} dateTime={publishedAt ?? undefined}>{releaseDate(publishedAt)}</time>
        <h3 className={styles.title}>{title}</h3>
      </header>
      <ul className={styles.items}>
        {items.map((it, i) => {
          const parts = splitLead(it.text)
          return (
            <li key={i} className={styles.item}>
              {it.image ? <img className={styles.image} src={releaseImageUrl(it.image)} alt="" loading="lazy" /> : null}
              <div className={styles.itemBody}>
                {parts ? <h4 className={styles.itemTitle}>{parts.lead}</h4> : null}
                <div className={styles.text}>{renderMarkdown(parts?.rest ?? it.text)}</div>
              </div>
            </li>
          )
        })}
      </ul>
    </article>
  )
}

/**
 * Содержимое окна выпуска: сам выпуск и «Понятно». Окно рисует шапку «Что нового» и крестик.
 * Фокус — сразу на «Понятно» (Enter закрывает окно), но без прокрутки: autoFocus прокручивал
 * окно к кнопке, и длинный выпуск открывался с конца.
 */
export function ReleaseBody({ onDone, ...view }: ViewProps & { onDone?: () => void }) {
  const done = useRef<HTMLButtonElement>(null)
  const closable = Boolean(onDone)
  useEffect(() => {
    if (closable) done.current?.focus({ preventScroll: true })
  }, [closable])
  return (
    <>
      <ReleaseView {...view} />
      <div className={styles.actions}>
        <button ref={done} type="button" className={styles.done} onClick={onDone}>Понятно</button>
      </div>
    </>
  )
}

/**
 * Карточка в доке: заголовок и явное действие «Посмотреть обновление».
 * Нажатие открывает выпуск; «Скрыть» в меню прячет карточку без открытия.
 * Без обработчиков — неживая копия для предпросмотра.
 */
export function ReleaseCard({ title, onOpen, onDismiss }: { title: string; onOpen?: () => void; onDismiss?: () => void }) {
  const live = !!onOpen
  const more = useRef<HTMLButtonElement>(null)
  const hide = useRef<HTMLButtonElement>(null)
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null)
  useEffect(() => {
    if (menu) hide.current?.focus({ preventScroll: true })
  }, [menu])
  const closeMenu = () => {
    if (document.activeElement === hide.current) more.current?.focus({ preventScroll: true })
    setMenu(null)
  }
  return (
    <div className={styles.card} inert={!live}>
      <button type="button" className={styles.cardOpen} onClick={onOpen} aria-label={`Посмотреть обновление: ${title}`}>
        <span className={styles.cardKicker}><Sparkles size={14} aria-hidden /> Что нового</span>
        <span className={styles.cardTitle}>{title}</span>
        <span className={styles.cardAction}>Посмотреть обновление <ArrowRight size={14} aria-hidden /></span>
      </button>
      <button
        ref={more}
        type="button"
        className={styles.cardMore}
        aria-label="Действия с обновлением"
        data-tooltip="Действия с обновлением"
        aria-haspopup="menu"
        aria-expanded={Boolean(menu)}
        onMouseDown={(e) => { if (menu) e.stopPropagation() }}
        onClick={(e) => {
          if (menu) { closeMenu(); return }
          const r = e.currentTarget.getBoundingClientRect()
          setMenu({ x: r.right, y: r.top - 48 })
        }}
      >
        <Ellipsis size={16} aria-hidden />
      </button>
      {menu ? (
        <ContextMenu x={menu.x} y={menu.y} align="right" label="Действия с обновлением" onClose={closeMenu}>
          <MenuItem ref={hide} icon={<EyeOff size={16} aria-hidden />} label="Скрыть" onClick={() => { setMenu(null); onDismiss?.() }} />
        </ContextMenu>
      ) : null}
    </div>
  )
}
